import React from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Flame, Trophy, MessageSquare, Send } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

const RoastGalleryTab = ({ 
  topRoaster, roastLeaderboard, roastFeed, roastForm, setRoastForm,
  submittingRoast, submitRoast, fireRoast, period 
}) => (
  <div className="space-y-6">
    {topRoaster && (
      <Card className="p-5 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-2xl">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl"><Flame className="w-8 h-8" /></div>
          <div>
            <p className="text-sm opacity-90">Top Roaster</p>
            <p className="text-xl font-bold">{topRoaster.user?.name || 'Unknown'}</p>
            <p className="text-sm opacity-80">@{topRoaster.user?.username} · {topRoaster.total_fires} fires · {topRoaster.roast_count} roasts</p>
          </div>
        </div>
      </Card>
    )}

    <Card className="p-5 rounded-2xl border-2 border-dashed border-red-200 dark:border-red-800">
      <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
        <Flame className="w-5 h-5 text-red-500" /> Submit a Roast
      </h3>
      <div className="space-y-3">
        <Input
          value={roastForm.target_post_id}
          onChange={(e) => setRoastForm(p => ({ ...p, target_post_id: e.target.value }))}
          placeholder="Target Post ID (e.g., post_37b8edf31109)"
          className="rounded-xl"
          data-testid="roast-target-input"
        />
        <Textarea
          value={roastForm.content}
          onChange={(e) => setRoastForm(p => ({ ...p, content: e.target.value }))}
          placeholder="Drop your roast here... (max 500 chars)"
          rows={3} maxLength={500} className="rounded-xl"
          data-testid="roast-content-input"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">{roastForm.content.length}/500</span>
          <Button onClick={submitRoast} disabled={submittingRoast}
            className="rounded-full bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold"
            data-testid="submit-roast-btn">
            <Flame className="w-4 h-4 mr-2" />
            {submittingRoast ? 'Submitting...' : 'Drop the Roast'}
          </Button>
        </div>
      </div>
    </Card>

    <Card className="rounded-2xl overflow-hidden">
      <div className="p-5 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-400" /> Top 10 Roasts · {period === 'month' ? 'This Month' : 'All Time'}
        </h3>
      </div>
      <div className="divide-y dark:divide-gray-700">
        {roastLeaderboard.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Flame className="w-10 h-10 mx-auto mb-2 text-gray-300" /><p>No roasts yet. Be the first!</p>
          </div>
        ) : roastLeaderboard.map((roast) => (
          <div key={roast.roast_id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition" data-testid={`roast-rank-${roast.rank}`}>
            <div className="flex items-start gap-3">
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                roast.rank === 1 ? 'bg-yellow-400 text-yellow-900' : roast.rank === 2 ? 'bg-gray-300 text-gray-700' : roast.rank === 3 ? 'bg-orange-300 text-orange-800' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}>{roast.rank}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm">{roast.author?.name || 'Unknown'}</span>
                  <span className="text-xs text-gray-500">@{roast.author?.username}</span>
                  <span className="text-xs text-gray-400">roasted @{roast.target_user?.username || '?'}</span>
                </div>
                <p className="text-gray-800 dark:text-gray-200 text-sm mb-2">{roast.content}</p>
                {roast.target_post_snippet && <p className="text-xs text-gray-400 italic mb-2 truncate">Re: "{roast.target_post_snippet}"</p>}
              </div>
              <button onClick={() => fireRoast(roast.roast_id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold transition-all ${roast.user_fired ? 'bg-red-500 text-white shadow-lg' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-red-100'}`}
                data-testid={`fire-roast-${roast.roast_id}`}>
                <Flame className="w-4 h-4" /><span>{roast.fire_count || 0}</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </Card>

    {roastFeed.length > 0 && (
      <Card className="rounded-2xl overflow-hidden">
        <div className="p-4 border-b dark:border-gray-700">
          <h3 className="font-bold flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Latest Roasts</h3>
        </div>
        <div className="divide-y dark:divide-gray-700">
          {roastFeed.map((roast) => (
            <div key={roast.roast_id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">{roast.author?.name}</span>
                    <span className="text-xs text-gray-400">roasted</span>
                    <span className="font-semibold text-sm">{roast.target_user?.name || '?'}</span>
                  </div>
                  <p className="text-gray-800 dark:text-gray-200 text-sm">{roast.content}</p>
                </div>
                <button onClick={() => fireRoast(roast.roast_id)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${roast.user_fired ? 'bg-red-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600'}`}>
                  <Flame className="w-3.5 h-3.5" /> {roast.fire_count || 0}
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    )}
  </div>
);

export default RoastGalleryTab;
